/**
 * useAiChat — SSE-streaming Claude client
 * =======================================
 * Talks to POST /api/ai/chat/stream and yields token deltas.
 *
 *   const { messages, send, streaming, error } = useAiChat({ surface, context });
 *   send("Your question here");
 *
 * `surface`: 'private' | 'admin' | 'bot' — routes to the right system prompt.
 * `context`: optional string injected into the system prompt for RAG-lite.
 */
import { useState, useRef, useCallback, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const STREAM_URL = `${API}/api/ai/chat/stream`;

function genSessionId() {
  return `tf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function useAiChat({ surface = 'private', context = null, storageKey } = {}) {
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const sessionIdRef = useRef(null);
  const abortRef = useRef(null);

  // Persist session id across reloads (per surface)
  useEffect(() => {
    if (!storageKey) {
      sessionIdRef.current = genSessionId();
      return;
    }
    try {
      const existing = window.sessionStorage.getItem(storageKey);
      if (existing) {
        sessionIdRef.current = existing;
      } else {
        const id = genSessionId();
        window.sessionStorage.setItem(storageKey, id);
        sessionIdRef.current = id;
      }
    } catch {
      sessionIdRef.current = genSessionId();
    }
  }, [storageKey]);

  const send = useCallback(
    async (userText) => {
      const text = (userText || '').trim();
      if (!text || streaming) return;
      setError(null);
      setStreaming(true);
      setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);

      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const res = await fetch(STREAM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({
            session_id: sessionIdRef.current || genSessionId(),
            message: text,
            surface,
            context: context || undefined,
          }),
        });
        if (!res.ok || !res.body) {
          throw new Error(`AI upstream ${res.status}`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';
          for (const raw of events) {
            const line = raw.trim();
            if (!line.startsWith('data:')) continue;
            const payload = line.slice(5).trim();
            if (!payload) continue;
            try {
              const parsed = JSON.parse(payload);
              if (parsed.delta) {
                setMessages((prev) => {
                  const copy = prev.slice();
                  const last = copy[copy.length - 1];
                  if (last && last.role === 'assistant') {
                    copy[copy.length - 1] = { role: 'assistant', content: last.content + parsed.delta };
                  }
                  return copy;
                });
              } else if (parsed.error) {
                setError(parsed.error);
              }
              // parsed.done → no-op, we just stop reading
            } catch {
              /* skip malformed frame */
            }
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network error');
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [surface, context, streaming],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
  }, []);

  const reset = useCallback(() => {
    setMessages([]);
    setError(null);
    sessionIdRef.current = genSessionId();
    if (storageKey) {
      try {
        window.sessionStorage.setItem(storageKey, sessionIdRef.current);
      } catch {
        /* ignore */
      }
    }
  }, [storageKey]);

  return { messages, send, stop, reset, streaming, error, sessionId: sessionIdRef.current };
}
